import { createVNode, render } from 'vue'
import type { App, Component } from 'vue'
import Popup from '@/components/base/popup/Popup.vue'

export interface ComponentPopupConfig {
  /** 业务组件 props */
  props?: Record<string, unknown>
  /** Popup 组件属性（无需传 visible） */
  popupProps?: {
    position?: 'center' | 'top' | 'bottom' | 'left' | 'right'
    showMask?: boolean
    maskClosable?: boolean
    wrapperClass?: string
    contentClass?: string
    animation?: 'fade' | 'slide' | 'scale' | 'auto'
    lockScroll?: boolean
  }
  /** 关闭回调 */
  onClose?: () => void
}

export interface ComponentPopupController {
  close: () => void
}

class ComponentPopupService {
  private app: App | null = null
  private currentVNode: any = null
  private currentContainer: HTMLElement | null = null

  setApp(app: App) {
    this.app = app
    return this
  }

  show(component: Component, config: ComponentPopupConfig = {}): ComponentPopupController {
    const { props = {}, popupProps = {}, onClose } = config

    this.hide()

    const container = document.createElement('div')
    document.body.appendChild(container)
    this.currentContainer = container

    let closed = false
    let destroyed = false
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null

    // 真正销毁：移除 DOM、卸载 VNode（在离场动画结束后调用）
    const destroy = () => {
      if (destroyed) return
      destroyed = true
      if (fallbackTimer) {
        clearTimeout(fallbackTimer)
        fallbackTimer = null
      }
      onClose?.()
      this.hide()
    }

    // 以指定 visible 重新渲染。对同一 container/同类型组件再次 render，
    // Vue 会执行 patch 更新 props 而非重新挂载，从而保留组件实例。
    const renderPopup = (visible: boolean) => {
      const vnode = createVNode(
        Popup,
        {
          visible,
          position: 'center',
          showMask: true,
          maskClosable: true,
          lockScroll: true,
          ...popupProps,
          'onUpdate:visible': (next: boolean) => {
            if (!next) close()
          },
          onClose: () => {
            close()
          },
          // Popup 离场动画结束后才真正销毁
          onClosed: () => {
            destroy()
          }
        },
        {
          default: () => createVNode(component, props)
        }
      )

      if (this.app) {
        vnode.appContext = this.app._context
      }

      this.currentVNode = vnode
      render(vnode, container)
    }

    const close = () => {
      if (closed) return
      closed = true
      // 先触发离场动画（visible -> false），等动画结束再销毁
      renderPopup(false)
      // 兜底：若因异常未收到 closed 事件，超时后强制销毁，避免泄漏
      fallbackTimer = setTimeout(destroy, 500)
    }

    renderPopup(true)

    return { close }
  }

  hide() {
    if (this.currentVNode && this.currentContainer) {
      render(null, this.currentContainer)
      if (this.currentContainer.parentNode) {
        this.currentContainer.parentNode.removeChild(this.currentContainer)
      }
      this.currentVNode = null
      this.currentContainer = null
    }
  }
}

const componentPopup = new ComponentPopupService()

export default componentPopup

export const showComponentPopup = (component: Component, config: ComponentPopupConfig = {}) => {
  return componentPopup.show(component, config)
}
